/**
 * Signatures at risk — V98-3-3 (lead / admin; research §2 rule 14, 23).
 * Non-delegable signatures that fall inside an approver's absence. Actions
 * by state:
 *   fits   (back before the hard date) → wait, or ask to sign remotely
 *   misses (back after the hard date)  → ask to sign remotely, or tell the
 *                                        customer + a new date (Calendar &
 *                                        trust center)
 * "Ask to sign remotely" is a delivered ask (POST /coverage/asks): asks reach
 * an absent approver on every surface and their clock starts at delivery.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import type { TodayView } from '@aigovops/core';
import { askToSignRemote } from '../../lib/coverage-api';
import { firstName, fmtDay, itemHref, nameOf, type Names } from '../../lib/today-model';
import { Chip, domId, errorText } from './shared';

type Row = NonNullable<TodayView['signatures_at_risk']>[number];

export function AtRiskCard({ rows, names, slug, prefix, focusLater, announce }: {
  rows: readonly Row[]; names: Names; slug: string; prefix: string; focusLater: (id: string) => void; announce: (m: string) => void;
}) {
  const [asked, setAsked] = useState<Record<string, string>>({});
  const ask = async (r: Row) => {
    const who = firstName(nameOf(names, r.approver_user_id, 'the approver'));
    try {
      // Non-delegable (certification package, system description): a sign_remote ask, never a delegation (S5).
      await askToSignRemote(slug, {
        item_id: r.request_id,
        message: `Can you sign "${r.title}" remotely before ${fmtDay(r.hard_date ?? r.due_on)}?`,
      });
      setAsked((s) => ({ ...s, [r.request_id]: `Asked ${who}` }));
      announce(`Asked ${who} to sign remotely`);
    } catch (e) {
      setAsked((s) => ({ ...s, [r.request_id]: errorText(e, 'The ask could not be sent.') }));
      announce('The ask could not be sent');
    }
    focusLater(domId('risk', r.request_id));
  };
  return (
    <ul className="gp-plain-list" aria-label="Signatures at risk">
      {rows.map((r) => {
        const who = nameOf(names, r.approver_user_id, 'Approver');
        return (
          <li key={r.request_id} className="gp-ow">
            <span className="gp-ow-main">
              <span className="gp-ow-line"><Link className="gp-ow-t" to={itemHref(prefix, { kind: 'signature_request', id: r.request_id })}>{r.title}</Link></span>
              <span className="gp-ow-meta">
                <span>{`${who} away ${fmtDay(r.absent_from)} – ${fmtDay(r.absent_to)}${r.absence_state === 'proposed' ? ' (proposed)' : ''} · back ${fmtDay(r.back_on)}`}</span>
                {r.fits
                  ? <Chip text={`Fits · hard date ${fmtDay(r.hard_date)}`} tone="ok" />
                  // R3-U9: one tone per fact — a projected miss is red everywhere (band, owners, here).
                  : <Chip text={`Will miss ${fmtDay(r.hard_date)}`} tone="red" />}
              </span>
            </span>
            <span className="gp-ow-act" id={domId('risk', r.request_id)} tabIndex={-1}>
              {asked[r.request_id]
                ? <Chip text={asked[r.request_id]!} tone="lock" />
                : <Button compact aria-label={`Ask to sign remotely · ${r.title}`} onClick={() => void ask(r)}>Ask to sign remotely</Button>}
              {!r.fits ? (
                <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/deliverables`} aria-label={`Tell customer · new date · ${r.title}`}>Tell customer · new date</Link>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
